import { Capacitor } from '@capacitor/core';
import api from './api';

/**
 * Push notification service for device token management
 */

// Dynamically import the push notifications plugin
let PushNotifications: typeof import('@capacitor/push-notifications').PushNotifications | null = null;

const initPushNotifications = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      const module = await import('@capacitor/push-notifications');
      PushNotifications = module.PushNotifications;
    } catch (error) {
      console.warn('Push notifications plugin not available:', error);
    }
  }
};

// Initialize on module load
initPushNotifications();

export interface DeviceToken {
  id: string;
  user_id: string;
  platform: string;
  device_id: string | null;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}

/**
 * Register the device for push notifications
 * This will request permission, register with APNs/FCM, and send the token to the backend
 */
export const registerForPushNotifications = async (): Promise<string | null> => {
  if (!PushNotifications) {
    // Push notifications not available on this platform
    return null;
  }

  try {
    // Check and request permission
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      // Push notification permission denied
      return null;
    }

    // Register with APNs/FCM
    await PushNotifications.register();

    // The token will be received via the 'registration' event
    // We'll set up the listener in the hook
    return 'registration_started';
  } catch (error) {
    console.error('Error registering for push notifications:', error);
    return null;
  }
};

/**
 * Register device token with the backend
 */
export const registerDeviceToken = async (
  token: string,
  platform: 'ios' | 'android',
  deviceId?: string
): Promise<DeviceToken> => {
  const response = await api.post<DeviceToken>('/api/v1/notifications/device-tokens', {
    token,
    platform,
    device_id: deviceId,
  });
  return response.data;
};

/**
 * Get all registered device tokens for the current user
 */
export const getDeviceTokens = async (): Promise<DeviceToken[]> => {
  const response = await api.get<DeviceToken[]>('/api/v1/notifications/device-tokens');
  return response.data;
};

/**
 * Unregister a device token
 */
export const unregisterDeviceToken = async (token: string): Promise<void> => {
  await api.post('/api/v1/notifications/device-tokens/unregister', { token });
};

/**
 * Delete a device token by ID
 */
export const deleteDeviceToken = async (tokenId: string): Promise<void> => {
  await api.delete(`/api/v1/notifications/device-tokens/${tokenId}`);
};

/**
 * Get the push notifications plugin instance
 */
export const getPushNotificationsPlugin = () => PushNotifications;

/**
 * Check if push notifications are supported on this platform
 */
export const isPushNotificationsSupported = (): boolean => {
  return Capacitor.isNativePlatform() && PushNotifications !== null;
};

